import type { ItemRefusal, QueuedTransaction } from './types'

/** What became of one queued transaction, as far as a screen needs to know. */
export type Settlement =
  /** The server took it. */
  | { state: 'synced' }
  /** The server refused it for good, with its reasons. */
  | { state: 'needs-attention', reason: string, refusals: ItemRefusal[] }
  /** No answer soon: offline, unreachable, or slow. The queue keeps it. */
  | { state: 'pending', reason?: string }

export type SettleOptions = {
  /** The row as it is now, or undefined once it is gone. */
  read: () => Promise<QueuedTransaction | undefined>
  /**
   * Ask the queue to push now. The flush the enqueue fired may have been
   * skipped by one already running, and that one did not know this row.
   */
  nudge?: () => void
  timeoutMs: number
  pollMs?: number
  now?: () => number
  sleep?: (ms: number) => Promise<void>
}

/**
 * Watch one transaction until the server has answered, or until it is clear no
 * answer is coming soon (decision 0008 addendum, 2026-09-03).
 *
 * The queue still owns the work: this reads the row and never changes it. A
 * first attempt that failed transiently shows as `attempts > 0` while still
 * pending — that is the offline case, reported at once rather than after the
 * timeout, so the strip can say "queued" without a pause.
 */
export async function awaitSettlement(options: SettleOptions): Promise<Settlement> {
  const { read, nudge, timeoutMs, pollMs = 150, now = Date.now, sleep = defaultSleep } = options
  const started = now()
  let polls = 0

  for (;;) {
    const row = await read()
    if (!row) return { state: 'pending' }
    if (row.state === 'synced') return { state: 'synced' }
    if (row.state === 'needs-attention') {
      return {
        state: 'needs-attention',
        reason: row.lastError ?? 'Refused by the server',
        refusals: row.refusals ?? [],
      }
    }
    if (row.attempts > 0) return { state: 'pending', reason: row.lastError }
    if (now() - started >= timeoutMs) return { state: 'pending' }

    // Not the first look: the enqueue's own flush has had its chance.
    if (polls > 0) nudge?.()
    polls += 1
    await sleep(pollMs)
  }
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}
